import { Card } from '../../../molecules/Card/Card';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { CalendarHeatmap, HeatScale } from '../../../organisms/CalendarHeatmap/CalendarHeatmap';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ActivityByDayAndHourView = {
  /** `Peak window` and `Strongest day`. */
  kpis: KPIMetric[];
  /** Weekday labels, Sunday first (the grid's rows). */
  rowLabels: readonly string[];
  /** Hour labels, midnight first (the grid's columns). */
  colLabels: readonly string[];
  /** `values[weekday][hour]` — engagement over the fixed eight weeks. */
  values: readonly (readonly number[])[];
  /** Whether any cell measured anything — a grid of nothing has no peak. */
  hasPeak: boolean;
  formatValue: (value: number) => string;
};

export type ActivityByDayAndHourCardProps = WidgetBlockProps & {
  view?: ActivityByDayAndHourView;
  /** Opens the conversations behind one cell (weekday row, hour column). */
  onViewDetails?: (row: number, col: number) => void;
};

/**
 * Community's heat grid (947:19575), and Overview's (972:19376).
 *
 * **The one card in the app that does not answer the date picker.** The grid has
 * 168 cells and a fortnight cannot fill them — five sixths would be empty and
 * the shape the card exists to show would be gone — so it reads a fixed rolling
 * eight weeks. The chips still apply: a filter that changes *what kind* of
 * conversation is counted is a question the grid can answer, one that changes
 * how many days are in it is not. Settled with the user.
 */
export function ActivityByDayAndHourCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: ActivityByDayAndHourCardProps) {
  const hasData = view !== undefined && view.hasPeak;
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No activity in this window"
      {...cardState(status, hasData, onRetry)}
      /*
       * Renamed from "Activity by day and hour" on the user's instruction
       * (2026-09-23). The component, its file and the widget id keep the old
       * name — they are referenced from saved reports, and renaming the id
       * would orphan every report already holding it.
       */
      title="Content performance"
      helpText="When this audience is actually posting, for every hour of every weekday."
      helpSupportingText="A rolling eight weeks, and the only card here that does not follow the date picker: the grid has 168 cells and a shorter range cannot fill them. The filter chips still apply. Cells are shaded against each other rather than against the busiest one, so a single viral post colours its own hour and no more."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <CalendarHeatmap
            onViewDetails={onViewDetails}
            rowLabels={view.rowLabels}
            colLabels={view.colLabels}
            values={view.values}
            height={PLOT_HEIGHT}
            unit="Engagement"
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={<HeatScale />}
    />
  );
}
